<template>
  <MainLayout>
    <ContentPageHeader functionName="アカウント管理" pageName="アカウント一覧">
    </ContentPageHeader>
    
    <div class="card">
      <!-- アカウント一覧テーブル -->
      <AccountTable
        :accounts="accounts"
        @edit="openEditModal"
        :user-authority="userAuthority"
      />
    </div>
    
    <!-- アカウント編集モーダル -->
    <CommonModal
      :visible="showEditModal"
      title="アカウント編集"
      ok-button-text="更新"
      @close="closeEditModal"
      @ok="updateAccount"
    >
      <div class="form-group">
        <label for="accountName">アカウント名 <span class="required">*</span></label>
        <input
          id="accountName"
          v-model="editingAccount.accountName"
          type="text"
          class="form-control"
          :class="{ 'is-invalid': validationErrors.accountName }"
          required
        />
        <div v-if="validationErrors.accountName" class="invalid-feedback">
          {{ validationErrors.accountName }}
        </div>
      </div>
      
      <div class="form-group">
        <label for="accountNameKana">アカウント名（カナ） <span class="required">*</span></label>
        <input
          id="accountNameKana"
          v-model="editingAccount.accountNameKana"
          type="text"
          class="form-control"
          :class="{ 'is-invalid': validationErrors.accountNameKana }"
          required
        />
        <div v-if="validationErrors.accountNameKana" class="invalid-feedback">
          {{ validationErrors.accountNameKana }}
        </div>
      </div>
      
      <div class="form-group">
        <label for="storeId">所属店舗 <span class="required">*</span></label>
        <select
          id="storeId"
          v-model="editingAccount.storeId"
          class="form-control"
          :class="{ 'is-invalid': validationErrors.storeId }"
          required
        >
          <option value="">選択してください</option>
          <option v-for="store in stores" :key="store.id" :value="store.id">
            {{ store.storeName }}
          </option>
        </select>
        <div v-if="validationErrors.storeId" class="invalid-feedback">
          {{ validationErrors.storeId }}
        </div>
      </div>
      
      <div class="form-group">
        <label for="authority">権限 <span class="required">*</span></label>
        <select
          id="authority"
          v-model="editingAccount.authority"
          class="form-control"
          :class="{ 'is-invalid': validationErrors.authority }"
          required
        >
          <option value="">選択してください</option>
          <option v-for="(name, value) in authorityOptions" :key="value" :value="Number(value)">
            {{ name }}
          </option>
        </select>
        <div v-if="validationErrors.authority" class="invalid-feedback">
          {{ validationErrors.authority }}
        </div>
      </div>
      
      <div class="form-group">
        <label for="loginId">ログインID <span class="required">*</span></label>
        <input
          id="loginId"
          v-model="editingAccount.loginId"
          type="text"
          class="form-control"
          :class="{ 'is-invalid': validationErrors.loginId }"
          required
        />
        <div v-if="validationErrors.loginId" class="invalid-feedback">
          {{ validationErrors.loginId }}
        </div>
      </div>
      
      <div class="form-group">
        <label for="password">パスワード <small class="text-muted">（変更する場合入力）</small></label>
        <input
          id="password"
          v-model="editingAccount.password"
          type="password"
          class="form-control"
          :class="{ 'is-invalid': validationErrors.password }"
        />
        <div v-if="validationErrors.password" class="invalid-feedback">
          {{ validationErrors.password }}
        </div>
      </div>
      
      <div class="form-group">
        <label for="passwordConfirm">パスワード確認</label>
        <input
          id="passwordConfirm"
          v-model="editingAccount.passwordConfirm"
          type="password"
          class="form-control"
          :class="{ 'is-invalid': validationErrors.passwordConfirm }"
        />
        <div v-if="validationErrors.passwordConfirm" class="invalid-feedback">
          {{ validationErrors.passwordConfirm }}
        </div>
      </div>
    </CommonModal>
  </MainLayout>
</template>

<script>
import MainLayout from '@/components/layout/MainLayout.vue'
import AccountTable from './AccountTable.vue'
import CommonModal from '@/components/common/CommonModal.vue'
import service from '../../services/service';
import { showLoading, hideLoading } from '@/utils/loadingEventBus'

export default {
  name: 'AccountListView',
  components: {
    MainLayout,
    AccountTable,
    CommonModal
  },
  data() {
    return {
      accounts: [],
      stores: [],
      loading: false,
      showEditModal: false,
      editingAccount: {
        id: null,
        accountName: '',
        accountNameKana: '',
        storeId: '',
        authority: '',
        loginId: '',
        password: '',
        passwordConfirm: ''
      },
      originalAccount: null,
      validationErrors: {},
      authorityOptions: {
        1: '管理者',
        2: '店長',
        3: 'スタッフ'
      }
    }
  },
  methods: {
    async fetchAccounts() {
      try {
        showLoading();
        const response = await service.getAllAccounts();
        if (response.data && response.data.success) {
          this.accounts = response.data.accounts || [];
        } else {
          console.error('アカウント一覧の取得に失敗しました:', response.data?.message);
        }
      } catch (error) {
        console.error('アカウント一覧の取得中にエラーが発生しました:', error);
        alert('アカウント一覧の取得中にエラーが発生しました。');
      } finally {
        hideLoading();
      }
    },
    
    async fetchStores() {
      try {
        showLoading();
        const response = await service.getStoreList();
        if (response.data) {
          this.stores = response.data.stores || [];
        }
      } catch (error) {
        console.error('店舗一覧の取得中にエラーが発生しました:', error);
        alert('店舗一覧の取得中にエラーが発生しました。');
      } finally {
        hideLoading();
      }
    },
    
    openEditModal(account) {
      this.editingAccount = {
        id: account.id,
        accountName: account.accountName,
        accountNameKana: account.accountNameKana,
        storeId: account.storeId,
        authority: account.authority,
        loginId: account.loginId || '',
        password: '',
        passwordConfirm: ''
      };
      this.originalAccount = { ...this.editingAccount };
      this.validationErrors = {};
      this.showEditModal = true;
    },
    
    closeEditModal() {
      this.showEditModal = false;
      this.editingAccount = {
        id: null,
        accountName: '',
        accountNameKana: '',
        storeId: '',
        authority: '',
        loginId: '',
        password: '',
        passwordConfirm: ''
      };
      this.validationErrors = {};
    },
    
    validationCheck() {
      if (!this.editingAccount.accountName) {
        return 'アカウント名は必須です';
      }
      
      if (!this.editingAccount.accountNameKana) {
        return 'アカウント名（カナ）は必須です';
      }
      
      if (!this.editingAccount.storeId) {
        return '所属店舗は必須です';
      }
      
      if (!this.editingAccount.authority) {
        return '権限は必須です';
      }
      
      if (!this.editingAccount.loginId) {
        return 'ログインIDは必須です';
      }

      // 桁数チェック
      if (64 < this.editingAccount.accountName.length) {
        return 'アカウント名は64文字以内で入力してください';
      }
      if (64 < this.editingAccount.accountNameKana.length) {
        return 'アカウント名（カナ）は64文字以内で入力してください';
      }
      if (32 < this.editingAccount.loginId.length) {
        return 'ログインIDは32文字以内で入力してください';
      }
      if (this.editingAccount.password && 256 < this.editingAccount.password.length) {
        return 'パスワードは256文字以内で入力してください';
      }

      
      // パスワードが入力されている場合、確認用パスワードとの一致をチェック
      if (this.editingAccount.password && this.editingAccount.password 
        !== this.editingAccount.passwordConfirm) {
        return 'パスワードと確認用パスワードが一致しません';
      }
    },
    
    async updateAccount() {
      const errorMessage = this.validationCheck();
      if (errorMessage != null) {
        alert(errorMessage);
        return;
      }
      
      // 確認ダイアログを表示
      if (!confirm('アカウント情報を更新します。\nよろしいですか。')) {
        return;
      }
      
      try {
        showLoading();
        const response = await service.updateAccount(this.editingAccount.id, {
          accountName: this.editingAccount.accountName,
          accountNameKana: this.editingAccount.accountNameKana,
          storeId: this.editingAccount.storeId,
          authority: this.editingAccount.authority,
          loginId: this.editingAccount.loginId,
          password: this.editingAccount.password
        });
        
        if (response.data && response.data.success) {
          alert('アカウント情報を更新しました');
          if (this.editingAccount.id === this.userId) {
            // 自分のアカウント情報を更新した場合、ログイン画面に遷移
            localStorage.removeItem('user');
            localStorage.removeItem('expiresAt');
            localStorage.removeItem('accountName');
            localStorage.removeItem('token');
            this.$router.push({ name: 'Login' });
          } else {
            // 他のアカウントを更新した場合、一覧を再取得
            this.closeEditModal();
            this.fetchAccounts();
          }
        } else {
          alert(response.data?.message || '');
        }
      } catch (error) {
        console.error('アカウント更新中にエラーが発生しました:', error);
        alert('アカウント更新中にエラーが発生しました');
      } finally {
        hideLoading();
      }
    }
  },
  computed: {
    // ユーザー情報の取得
    userAuthority() {
      console.log("this.$store.getters['auth/currentUser']", this.$store.getters['auth/currentUser']);
      return this.$store.getters['auth/currentUser']?.authority
    },
    userId() {
      return this.$store.getters['auth/currentUser']?.id
    },
  },
  mounted() {
    this.fetchAccounts();
    this.fetchStores();
  }
}
</script>

<style scoped>
.content-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.card {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.form-control {
  display: block;
  width: 100%;
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #495057;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.form-control:focus {
  color: #495057;
  background-color: #fff;
  border-color: #80bdff;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.is-invalid {
  border-color: #dc3545;
}

.invalid-feedback {
  display: block;
  width: 100%;
  margin-top: 0.25rem;
  font-size: 80%;
  color: #dc3545;
}

.required {
  color: #dc3545;
}
</style>
